
import { useState, useEffect } from 'react';
import { 
  questions, 
  calculatePersonalityType, 
  PersonalityType, 
  Question, 
  CareerPreferences,
  MBTIResult
} from '@/utils/personalityTypes';

type TestResult = { 
  personalityType: PersonalityType, 
  careerPreferences: CareerPreferences,
  mbtiResult: MBTIResult 
};

interface UsePersonalityTestProps {
  onComplete: (result: TestResult) => void;
  isAuthenticated: boolean;
}

export const usePersonalityTest = ({ onComplete, isAuthenticated }: UsePersonalityTestProps) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [questionType, setQuestionType] = useState<'personality' | 'career'>('personality');
  const [mbtiPhase, setMbtiPhase] = useState<'EI' | 'SN' | 'TF' | 'JP' | 'career'>('EI');
  
  // Update progress bar
  useEffect(() => {
    setProgressPercentage((currentQuestionIndex / questions.length) * 100);
  }, [currentQuestionIndex]);
  
  const currentQuestion = questions[currentQuestionIndex];
  
  // Update question type and MBTI phase for section heading
  useEffect(() => {
    if (currentQuestion) {
      setQuestionType(currentQuestion.questionType || 'personality');
      
      if (currentQuestion.mbtiDimension) {
        setMbtiPhase(currentQuestion.mbtiDimension.dimension);
      } else if (currentQuestion.questionType === 'career') {
        setMbtiPhase('career');
      }
    }
  }, [currentQuestion]);
  
  const handleAnswer = (questionId: number, value: number) => {
    if (isTransitioning) return;
    
    if (!isAuthenticated) {
      document.dispatchEvent(new CustomEvent('open-auth'));
      return;
    }
    
    setIsTransitioning(true);
    
    // Update answers
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Add a delay for the transition effect
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        handleTestCompletion();
      }
      setIsTransitioning(false);
    }, 300);
  };
  
  const handleTestCompletion = () => {
    setIsCompleted(true);
    const result = calculatePersonalityType(answers);
    onComplete(result);
  };

  return {
    currentQuestionIndex,
    currentQuestion,
    isCompleted,
    isTransitioning,
    progressPercentage,
    questionType,
    mbtiPhase,
    questions,
    handleAnswer
  };
};
